import { WORKFLOW_LIMITS } from "@sagot/constants";
import type { useTranslation } from "@sagot/i18n";
import { effectiveFallback, exitSlotsOf, type ExitFallback, type ExitSlot } from "../exit-slots";
import { isConnectorCall, type Step } from "../types";
import { isKeyedTarget, saveTargetOf, targetKey } from "./collect-model";
import { bodyAllowed, bodyProblem } from "./connector-model";
import { nameOf, type BuilderRefs } from "./refs";
import { isTypedKeyTarget, replyProblems, replySaveOf, snoozeModeOf } from "./reply-model";
import { summarizeSystem, systemIncompleteOf } from "./system/system-model";
import { serviceIncompleteOf, summarizeService } from "../service/service-model";

type TFunction = ReturnType<typeof useTranslation>["t"];

const P = "engage.workflows.summaries";
const F = "engage.workflows.fields";

/** One line that says what a step does, shown on its card. */
export function summarize(step: Step, refs: BuilderRefs, t: TFunction): string {
  const unknown = t(`${P}.unknown`);
  switch (step.type) {
    case "send_webhook":
      return t(`${P}.send_webhook`, { endpoint: nameOf(refs.endpoints, step.endpoint_id, unknown) });
    case "add_tags":
    case "remove_tags":
      return step.tags.length > 0 ? step.tags.join(", ") : unknown;
    case "assign":
      if (step.to === "unassign") return t(`${P}.unassign`);
      return step.to === "team"
        ? t(`${P}.assignTeam`, { name: nameOf(refs.teams, step.team_id, unknown) })
        : t(`${P}.assignUser`, { name: nameOf(refs.members, step.user_id, unknown) });
    case "set_status":
      return step.status === "closed" || step.status === "resolved" ? t(`${P}.status_${step.status}`) : t(`${P}.set_status`, { status: t(`${F}.statuses.${step.status}`) });
    case "add_note":
      return step.text || unknown;
    case "update_lead":
      return leadSummary(step, refs, t) || unknown;
    case "notify_staff":
      return t(`${P}.notify`, { who: t(`${F}.notifyTargets.${step.to}`), title: step.title || unknown });
    case "branch":
      return t(`${P}.branch`, { count: step.branches.length, else: step.else_goto ?? t(`${P}.endRun`) });
    case "apply_rules":
      return t(`${P}.rules`, { count: step.rules.length });
    case "go_to_path":
      return t(`${P}.go_to_path`, { path: step.path });
    case "end":
      return t(`${P}.end`);
    case "send_message":
    case "reply_buttons":
      return step.text || unknown;
    case "collect_data": {
      const { target, key } = saveTargetOf(step.save_to);
      const label = t(`${F}.saveTargets.${targetKey(target)}`);
      const where = isKeyedTarget(target) && key ? `${label} (${key})` : label;
      return t(`${P}.collect`, { question: step.text || unknown, type: t(`${F}.fieldTypes.${step.field.type}`), target: where });
    }
    case "let_assistant_answer":
      return step.guidance ? t(`${P}.answerGuided`, { guidance: step.guidance }) : t(`${P}.answer`);
    case "show_booking":
      return t(`${P}.booking`, { action: nameOf(refs.actions, step.action_id, unknown) });
    case "show_lead_form":
      return t(`${P}.leadForm`, { action: nameOf(refs.actions, step.action_id, unknown) });
    case "hand_to_person":
      return handSummary(step, refs, t);
    case "expected_reply_time":
      return t(`${P}.replyTime`);
    case "ask_rating":
      return t(`${P}.rating`, { scale: t(`${F}.ratingScales.${step.scale}`), question: step.text || unknown });
    case "collect_reply":
      return t(`${P}.collect_reply`, { question: step.text || unknown, target: replyTargetLabel(step.save_to, refs, t) });
    case "verify_email":
      return t(`${P}.verify_email`, { message: step.text || unknown });
    case "disable_reply":
      return t(`${P}.disable_reply`);
    case "enable_reply":
      return t(`${P}.enable_reply`);
    case "snooze":
      return snoozeSummary(step, t);
    case "set_language":
      return t(`${P}.set_language`, { language: t(`${F}.languages.${step.language}`) });
    case "wait": {
      if (!step.duration) return t(`${P}.waitUntilOpen`);
      const wait = t(`${P}.wait_${step.duration.unit}`, { count: step.duration.value });
      return step.until_office_hours ? t(`${P}.waitUntilOffice`, { wait }) : wait;
    }
    case "wait_for_webhook":
      return t(`${P}.wait_for_webhook`, { count: step.timeout_hours });
    case "run_workflow":
      return t(`${P}.run_workflow`, { workflow: nameOf(refs.reusables, step.workflow_id, unknown) });
    case "http_request":
      if (isConnectorCall(step)) return step.operation_key ? t(`${P}.http_request_operation`, { operation: step.operation_key }) : unknown;
      return t(`${P}.http_request`, { method: step.method, path: step.path?.length ? step.path : "/", endpoint: nameOf(refs.endpoints, step.endpoint_id, unknown) });
    case "show_article":
      return t(`${P}.show_article`, { source: nameOf(refs.sources ?? [], step.source_id, unknown) });
    case "add_summary_note":
      return t(`${P}.add_summary_note${step.notify_assignee ? "_notify" : ""}`);
    case "apply_sla":
      return t(`${P}.apply_sla`, { policy: nameOf(refs.slaPolicies, step.policy_id, unknown) });
    case "set_priority":
      return t(`${P}.set_priority`, { priority: t(`engage.inbox.priority.${step.priority}`) });
    case "email_visitor":
      return t(`${P}.email_visitor`, { subject: step.subject || unknown });
    case "set_attribute":
      return t(`${P}.set_attribute`, { target: attributeLabel(step, refs, unknown), value: step.value || unknown });
    case "notify_team":
    case "route_handoff":
    case "assign_lead_owner":
    case "send_to_subscribed_endpoints":
      return summarizeSystem(step, refs, t);
    case "create_ticket":
    case "update_ticket":
    case "set_ticket_state":
    case "route_ticket":
    case "assign_ticket":
    case "add_ticket_note":
    case "reply_to_ticket":
    case "notify_contact":
    case "link_ticket":
    case "apply_ticket_sla":
    case "request_csat":
    case "ticket_intake":
    case "show_ticket_form":
    case "show_my_tickets":
    case "lookup_asset":
    case "schedule_visit":
    case "lookup_catalogue":
    case "check_order_status":
    case "show_catalogue":
    case "create_order":
    case "show_order_status":
      return summarizeService(step, refs, t);
  }
}

/** Where a collected reply goes, written for the author: "Visitor e-mail", "Variable (student_no)", the attribute's name. */
function replyTargetLabel(saveTo: string, refs: BuilderRefs, t: TFunction): string {
  const save = replySaveOf(saveTo);
  if (save.target === "attribute") {
    const definition = refs.attributes.find((item) => item.object === save.object && item.key === save.key);
    return definition?.label ?? (save.key ? `${save.object}.${save.key}` : t(`${P}.unknown`));
  }
  const label = t(`${F}.saveTargets.${targetKey(save.target)}`);
  return isTypedKeyTarget(save.target) && save.key ? `${label} (${save.key})` : label;
}

function snoozeSummary(step: Extract<Step, { type: "snooze" }>, t: TFunction): string {
  const mode = snoozeModeOf(step);
  if (mode === "until_office_hours") return t(`${P}.snooze_office`);
  if (mode === "until") return t(`${P}.snooze_until`, { date: step.until?.date ?? "", time: step.until?.time ?? "" });
  return t(`${P}.snooze_${step.for?.unit ?? "hours"}`, { count: step.for?.value ?? 0 });
}

function attributeLabel(step: Extract<Step, { type: "set_attribute" }>, refs: BuilderRefs, fallback: string): string {
  const definition = refs.attributes.find((item) => item.object === step.object && item.key === step.key);
  return definition?.label ?? (step.key ? `${step.object}.${step.key}` : fallback);
}

function leadSummary(step: Extract<Step, { type: "update_lead" }>, refs: BuilderRefs, t: TFunction): string {
  const parts: string[] = [];
  if (step.stage_id) parts.push(t(`${P}.leadStage`, { stage: nameOf(refs.stages, step.stage_id, "?") }));
  if (step.owner_user_id) parts.push(t(`${P}.leadOwner`, { name: nameOf(refs.members, step.owner_user_id, "?") }));
  if (step.sync_crm) parts.push(t(`${P}.leadSync`));
  return parts.join(" · ");
}

function handSummary(step: Extract<Step, { type: "hand_to_person" }>, refs: BuilderRefs, t: TFunction): string {
  const unknown = t(`${P}.unknown`);
  const target =
    step.to === "team"
      ? t(`${P}.assignTeam`, { name: nameOf(refs.teams, step.team_id, unknown) })
      : step.to === "user"
        ? t(`${P}.assignUser`, { name: nameOf(refs.members, step.user_id, unknown) })
        : t(`${P}.handDefault`);
  return step.reason ? `${target} · ${step.reason}` : target;
}

/** What an exit is called: the author's own label (a button, a branch), else the outcome's name, numbered for buttons and branches. */
export function exitName(slot: ExitSlot, t: TFunction): string {
  if (slot.label) return slot.label;
  if (slot.labelParams) return t(slot.labelKey, slot.labelParams);
  return slot.ordinal === undefined ? t(slot.labelKey) : t(slot.labelKey, { n: slot.ordinal });
}

/** A path a step leads to, with the name of the way there (button, branch, outcome). */
export interface StepExit {
  label: string;
  to?: string;
  /** Only with `{ all: true }`: what the run does when the exit is not connected. */
  fallback?: ExitFallback;
}

/**
 * The exits a card shows under its summary (`label → path`): the required ones and the connected optional ones, or with
 * `{ all: true }` every exit the step has, each with what it does when not connected (on the last step of a path, with
 * `lastStep`, carrying on means the workflow ends). A "Go to path" step says where it goes in its summary, so it adds no exit.
 */
export function exitsOf(step: Step, t: TFunction, options: { all?: boolean; lastStep?: boolean } = {}): StepExit[] {
  return exitSlotsOf(step)
    .filter((slot) => slot.key !== "path" && (options.all === true || slot.required || slot.target !== undefined))
    .map((slot) => (options.all === true ? { label: exitName(slot, t), to: slot.target, fallback: effectiveFallback(slot, options.lastStep ?? false) } : { label: exitName(slot, t), to: slot.target }));
}

function incompleteReply(step: Extract<Step, { type: "collect_reply" }>): string | null {
  if (!step.text.trim()) return "message";
  const save = replySaveOf(step.save_to);
  if (save.target === "attribute" && !save.key) return "attribute";
  if ((save.target === "lead" || save.target === "var") && !save.key) return "saveKey";
  const [problem] = replyProblems(step);
  return problem ? `reply_${problem}` : null;
}

/**
 * What a customer-facing step still needs before it can work (the `builder.incomplete` key), or `null`.
 * Only a hint on the card: the server checks the saved draft.
 */
export function incompleteOf(step: Step): string | null {
  switch (step.type) {
    case "send_message":
      return step.text.trim() ? null : "message";
    case "reply_buttons":
      if (!step.text.trim()) return "message";
      if (step.buttons.some((button) => !button.label.trim())) return "buttonLabels";
      return step.buttons.some((button) => !button.goto) ? "buttonPaths" : null;
    case "collect_data": {
      if (!step.text.trim()) return "message";
      if (!step.field.label.trim()) return "fieldLabel";
      if (step.field.type === "dropdown" && (step.field.options ?? []).length < WORKFLOW_LIMITS.dropdownOptionsMin) return "options";
      const { target, key } = saveTargetOf(step.save_to);
      return isKeyedTarget(target) && !key ? "saveKey" : null;
    }
    case "show_booking":
    case "show_lead_form":
      return step.action_id ? null : "action";
    case "hand_to_person":
      return (step.to === "team" && !step.team_id) || (step.to === "user" && !step.user_id) ? "person" : null;
    case "ask_rating":
    case "verify_email":
      return step.text.trim() ? null : "message";
    case "collect_reply":
      return incompleteReply(step);
    case "snooze":
      return step.until && (!step.until.date || !step.until.time) ? "snoozeUntil" : null;
    case "show_article":
      return step.source_id ? null : "article";
    case "apply_sla":
      return step.policy_id ? null : "policy";
    case "run_workflow":
      return step.workflow_id ? null : "workflow";
    case "http_request":
      if (isConnectorCall(step)) return step.connector_id && step.operation_key ? null : "operation";
      if (!step.endpoint_id) return "endpoint";
      return bodyAllowed(step.method) && bodyProblem(step.body) !== null ? "httpBody" : null;
    case "email_visitor":
      if (!step.subject.trim()) return "emailSubject";
      return step.body.trim() ? null : "emailBody";
    case "set_attribute":
      if (!step.key) return "attribute";
      return step.value.trim() ? null : "attributeValue";
    case "notify_team":
    case "route_handoff":
    case "assign_lead_owner":
    case "send_to_subscribed_endpoints":
      return systemIncompleteOf(step);
    case "create_ticket":
    case "update_ticket":
    case "set_ticket_state":
    case "route_ticket":
    case "assign_ticket":
    case "add_ticket_note":
    case "reply_to_ticket":
    case "notify_contact":
    case "link_ticket":
    case "apply_ticket_sla":
    case "request_csat":
    case "ticket_intake":
    case "show_ticket_form":
    case "show_my_tickets":
    case "lookup_asset":
    case "schedule_visit":
    case "lookup_catalogue":
    case "check_order_status":
    case "show_catalogue":
    case "create_order":
    case "show_order_status":
      return serviceIncompleteOf(step);
    default:
      return null;
  }
}
